/**
 * How a room's messages travel: over a WebSocket to the relay (`packages/relay`), or between the
 * tabs of one browser over a BroadcastChannel that plays the relay's part (`?relay=local`: two tabs
 * on one machine, and the e2e suite). Both deliver the same two things to the session: the relay's
 * notices (welcome, join, leave, error) and the other players' messages with their peer number.
 * The session (`session.ts`) never knows which one it has.
 */

import { assignPeer, CLOSE, CLOSE_REASONS, decodeWire, type Role, type RoomNotice } from "@lache/relay/room";

export interface TransportEvents {
  notice(n: RoomNotice): void;
  message(from: number, text: string): void;
  /** The connection ended, with the relay's close code (4xxx) or the socket's (1000 clean, 1006 lost). */
  closed(code: number, reason: string): void;
}

export interface Transport {
  readonly kind: "relay" | "local";
  send(text: string): void;
  /** Only "left" gives up the seat; reconnecting preserves its relay reservation. */
  close(reason?: "left" | "reconnecting"): void;
}

/** Sent every so often so a quiet socket is not dropped by something on the way; the relay answers "!". */
const KEEPALIVE_MS = 20_000;
const RESPONSE_TIMEOUT_MS = 10_000;

/** A room over the relay. `url` is the relay's origin (`wss://...`). */
export function connectRelay(
  url: string,
  code: string,
  role: Role,
  events: TransportEvents,
  resumeToken?: string,
): Transport {
  // The private seat token never goes in a URL, room link, or another player's messages.
  const protocols = resumeToken ? ["lache-v2", `resume.${resumeToken}`] : ["lache-v2"];
  const ws = new WebSocket(
    `${url.replace(/\/$/, "")}/room/${encodeURIComponent(code)}?role=${role}`,
    protocols,
  );
  let keepalive: ReturnType<typeof setInterval> | null = null;
  let responseTimer: ReturnType<typeof setTimeout> | null = null;
  let done = false;
  const finish = (code: number, reason: string): void => {
    if (done) return;
    done = true;
    if (keepalive) clearInterval(keepalive);
    if (responseTimer) clearTimeout(responseTimer);
    events.closed(code, reason);
  };
  const lost = (): void => {
    try {
      ws.close(1000, "reconnecting");
    } catch {
      // A connection which never opened may already be closing.
    }
    finish(1006, "connection lost");
  };
  // A suspended network can leave a socket CONNECTING or OPEN without ever firing close.
  responseTimer = setTimeout(lost, RESPONSE_TIMEOUT_MS);
  ws.addEventListener("open", () => {
    if (done) return;
    keepalive = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send("?");
        if (responseTimer) clearTimeout(responseTimer);
        responseTimer = setTimeout(lost, RESPONSE_TIMEOUT_MS);
      }
    }, KEEPALIVE_MS);
  });
  ws.addEventListener("message", (e) => {
    if (done) return;
    const text = String(e.data);
    const wire = decodeWire(text);
    if (text === "!" || (wire?.kind === "notice" && wire.notice.t === "welcome")) {
      if (responseTimer) clearTimeout(responseTimer);
      responseTimer = null;
    }
    if (!wire) return;
    if (wire.kind === "notice") {
      events.notice(wire.notice);
      if (wire.notice.t === "error") {
        // A refused upgrade can stay CLOSING without delivering the TCP close event. The relay's
        // error is authoritative: preserve its terminal code instead of timing out and retrying.
        finish(wire.notice.code, wire.notice.reason);
        try {
          ws.close(wire.notice.code, wire.notice.reason);
        } catch {
          // Already closing; the logical connection has ended and all its timers are cleared.
        }
      }
    } else events.message(wire.from, wire.text);
  });
  ws.addEventListener("close", (e) => finish(e.code, e.reason || CLOSE_REASONS[e.code] || ""));
  ws.addEventListener("error", () => {
    // The close event follows with 1006; a socket that never opened ends there too.
  });
  return {
    kind: "relay",
    send: (text) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(text);
    },
    close: (reason = "left") => {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) ws.close(1000, reason);
      finish(1000, reason);
    },
  };
}

/** The frames on a local room's channel. `to` addresses one tab by its id. */
type LocalFrame =
  | { k: "claim"; id: string }
  | { k: "taken"; to: string }
  | { k: "hello"; id: string }
  | { k: "welcome"; to: string; peer: number; peers: number[] }
  | { k: "refuse"; to: string; code: number }
  | { k: "join"; peer: number; id: string }
  | { k: "leave"; peer: number; host: boolean }
  | { k: "m"; from: number; text: string };

/** How long a host waits for another host to object, and a guest for the host's welcome. */
const CLAIM_MS = 250;
const WELCOME_MS = 1500;

/**
 * A room between the tabs of one browser: the host tab keeps the seats and welcomes the guests,
 * every tab hears every message on the channel and keeps the ones for it. Same semantics as the
 * relay's room, including the close codes.
 */
export function connectLocal(code: string, role: Role, events: TransportEvents): Transport {
  const channel = new BroadcastChannel(`lache-room-${code}`);
  const id = Math.random().toString(36).slice(2, 12);
  let myPeer = -1;
  let done = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  /** Host only: the seat of every guest by its tab id. */
  const seats = new Map<string, number>();
  const post = (f: LocalFrame): void => channel.postMessage(f);
  const finish = (closeCode: number, reason: string): void => {
    if (done) return;
    done = true;
    if (timer) clearTimeout(timer);
    window.removeEventListener("pagehide", leave);
    channel.close();
    events.closed(closeCode, reason);
  };
  const leave = (): void => {
    if (done) return;
    if (myPeer >= 0) post({ k: "leave", peer: myPeer, host: role === "host" });
    finish(1000, "left");
  };
  window.addEventListener("pagehide", leave);
  channel.addEventListener("message", (e: MessageEvent<LocalFrame>) => {
    const f = e.data;
    if (done || typeof f !== "object" || f === null) return;
    if (role === "host" && myPeer === 0) {
      if (f.k === "claim") post({ k: "taken", to: f.id });
      else if (f.k === "hello") {
        const seat = assignPeer("guest", [0, ...seats.values()]);
        if ("close" in seat) {
          post({ k: "refuse", to: f.id, code: seat.close });
          return;
        }
        const peers = [0, ...seats.values()];
        seats.set(f.id, seat.peer);
        post({ k: "welcome", to: f.id, peer: seat.peer, peers });
        post({ k: "join", peer: seat.peer, id: f.id });
        events.notice({ t: "join", peer: seat.peer });
      } else if (f.k === "leave" && !f.host) {
        for (const [tab, peer] of seats) if (peer === f.peer) seats.delete(tab);
        events.notice({ t: "leave", peer: f.peer });
      } else if (f.k === "m") events.message(f.from, f.text);
      return;
    }
    if (role === "host") {
      // Still claiming: another host objects.
      if (f.k === "taken" && f.to === id) finish(CLOSE.ROOM_TAKEN, CLOSE_REASONS[CLOSE.ROOM_TAKEN] ?? "");
      return;
    }
    // A guest.
    if (myPeer < 0) {
      if (f.k === "welcome" && f.to === id) {
        if (timer) clearTimeout(timer);
        myPeer = f.peer;
        events.notice({ t: "welcome", peer: f.peer, peers: f.peers, code });
      } else if (f.k === "refuse" && f.to === id) finish(f.code, CLOSE_REASONS[f.code] ?? "");
      return;
    }
    if (f.k === "join" && f.id !== id) events.notice({ t: "join", peer: f.peer });
    else if (f.k === "leave" && f.peer !== myPeer) {
      events.notice({ t: "leave", peer: f.peer });
      if (f.host) finish(CLOSE.HOST_LEFT, CLOSE_REASONS[CLOSE.HOST_LEFT] ?? "");
    } else if (f.k === "m" && f.from !== myPeer) events.message(f.from, f.text);
  });
  if (role === "host") {
    post({ k: "claim", id });
    timer = setTimeout(() => {
      if (done) return;
      myPeer = 0;
      events.notice({ t: "welcome", peer: 0, peers: [], code });
    }, CLAIM_MS);
  } else {
    post({ k: "hello", id });
    timer = setTimeout(() => finish(CLOSE.NO_ROOM, CLOSE_REASONS[CLOSE.NO_ROOM] ?? ""), WELCOME_MS);
  }
  return {
    kind: "local",
    send: (text) => {
      if (!done && myPeer >= 0) post({ k: "m", from: myPeer, text });
    },
    close: leave,
  };
}
